import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Link2, Undo2, Redo2 } from "lucide-react";
import { compressImageFile } from "@/lib/imageCompress";

/**
 * Lightweight rich-text editor built on contentEditable.
 * - Bold / Italic / Bullet / Ordered / Link buttons
 * - Paste-image support: pasted screenshots are auto-compressed (≤ ~900 KB JPEG,
 *   longest edge ≤ 1600 px) before being embedded as inline <img src="data:...">
 * - Emits raw HTML via onChange (safe to store; render with dangerouslySetInnerHTML)
 *
 * NOTE: The output is user-generated HTML. When you render it back, either use
 * dangerouslySetInnerHTML inside a sandboxed container OR sanitize with DOMPurify.
 * For the Reachvel CR module, only admin+auth'd client see it; risk is limited.
 */
export default function LightRichEditor({ value = "", onChange, placeholder = "Type here…", height = 200, testid }) {
  const ref = useRef(null);
  const lastValue = useRef("");

  // Sync incoming value into the DOM only when it changes externally
  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastValue.current) {
      ref.current.innerHTML = value || "";
      lastValue.current = value || "";
    }
  }, [value]);

  const emit = () => {
    const html = ref.current?.innerHTML || "";
    lastValue.current = html;
    onChange?.(html);
  };

  const exec = (cmd, val) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val || undefined);
    emit();
  };

  const insertCompressedImage = async (file) => {
    try {
      const { dataUrl } = await compressImageFile(file);
      ref.current?.focus();
      document.execCommand(
        "insertHTML",
        false,
        `<img src="${dataUrl}" style="max-width:100%; border-radius:6px; margin:8px 0;" />`,
      );
      emit();
    } catch {
      // Fallback — insert original if compression fails
      const reader = new FileReader();
      reader.onload = () => {
        ref.current?.focus();
        document.execCommand(
          "insertHTML",
          false,
          `<img src="${reader.result}" style="max-width:100%; border-radius:6px; margin:8px 0;" />`,
        );
        emit();
      };
      reader.readAsDataURL(file);
    }
  };

  const onPaste = (e) => {
    // Handle pasted images from clipboard — compress before embedding
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) insertCompressedImage(file);
        return;
      }
    }
    // Otherwise plain text paste (strip formatting)
    if (e.clipboardData) {
      const text = e.clipboardData.getData("text/plain");
      if (text) {
        e.preventDefault();
        document.execCommand("insertText", false, text);
        emit();
      }
    }
  };

  const onDrop = (e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    e.preventDefault();
    images.forEach((f) => insertCompressedImage(f));
  };

  return (
    <div className="light-rich-editor border border-black/15 rounded-md bg-white">
      <div className="flex items-center gap-1 border-b border-black/10 px-2 py-1.5 bg-[#f7f6f3]">
        <ToolbarBtn onClick={() => exec("bold")}     title="Bold"          testid={`${testid}-bold`}><Bold className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("italic")}   title="Italic"        testid={`${testid}-italic`}><Italic className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-4 bg-black/15 mx-1" />
        <ToolbarBtn onClick={() => exec("insertUnorderedList")} title="Bullet list" testid={`${testid}-ul`}><List className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("insertOrderedList")}   title="Numbered list" testid={`${testid}-ol`}><ListOrdered className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-4 bg-black/15 mx-1" />
        <ToolbarBtn onClick={() => {
          const url = window.prompt("Link URL:");
          if (url) exec("createLink", url);
        }} title="Insert link" testid={`${testid}-link`}><Link2 className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-4 bg-black/15 mx-1" />
        <ToolbarBtn onClick={() => exec("undo")}     title="Undo"          testid={`${testid}-undo`}><Undo2 className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("redo")}     title="Redo"          testid={`${testid}-redo`}><Redo2 className="h-3.5 w-3.5" /></ToolbarBtn>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.15em] text-[#9a9a9a]">Paste screenshots directly</span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emit}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        data-testid={testid}
        className="rich-editor-body px-3 py-2 text-sm outline-none overflow-y-auto"
        style={{ minHeight: height, maxHeight: 600 }}
      />
      <style>{`
        .rich-editor-body:empty::before {
          content: attr(data-placeholder);
          color: #9a9a9a;
          pointer-events: none;
        }
        .rich-editor-body img { max-width: 100%; height: auto; }
        .rich-editor-body ul, .rich-editor-body ol { padding-left: 22px; margin: 6px 0; }
        .rich-editor-body a { color: #ff5722; text-decoration: underline; }
      `}</style>
    </div>
  );
}

function ToolbarBtn({ children, onClick, title, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault() /* keep selection */}
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-1.5 rounded hover:bg-black/5 text-[#4a4a4a] hover:text-[#0a0a0a]"
    >
      {children}
    </button>
  );
}
